<template>
  <div class="m-4">
    <template v-if="!isReadOnly">
      <div
        class="alert"
        :class="hasSteeringErrors.length ? 'alert-danger' : 'alert-warning'">
        <div class="flex flex-column flex-grow">
          <div class="flex flex-row align-items-center mb-2 icon-sm">
            <icon-warning-hexagon />
            <span class="ml-3 font-weight-bold">{{
              capitalizeTranslation('messages.dealSpendSteeringRestrictions')
            }}</span>
          </div>
          <div class="mt-2">
            <ul>
              <li
                v-for="message in warningMessages"
                :key="message">
                {{ message }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </template>
    <div class="spend-steering-tables">
      <div
        v-if="!isReadOnly"
        class="available-dimensions"
        :style="adaptedCardSizeStyle">
        <div class="table-heading">
          <span>
            {{ capitalizeTranslation('messages.dimensionState', [i18n.t('states.available'), dimensionType]) }}
          </span>
          <div>
            <span>{{ capitalizeTranslation('names.weight') }}: </span>
            <span
              :class="{
                'text-danger':
                  dealSpendSteering.remainingSteeringPercentage === 0 && selectableDimensionEntities.length > 0,
              }"
              >{{ dealSpendSteering.remainingSteeringPercentageFormatted }}</span
            >
          </div>
        </div>
        <div class="available-dimension-list">
          <div class="input-container m-3">
            <div class="icon">
              <icon-search />
            </div>
            <input
              v-model="searchInput"
              name="filterText"
              type="text"
              :placeholder="searchForEntity"
              class="form-control table-text-filter pr-5"
              maxlength="191" />
            <div class="delete icon-badge">
              <div
                v-if="searchInput.trim().length > 0"
                @click="searchInput = ''">
                <IconCircleX />
              </div>
            </div>
          </div>
          <div class="select-all-container flex flex-row mx-3 mb-3 justify-content-end">
            <button
              id="select-all-dimensions"
              class="btn btn-xs btn-info-hollow mr-2"
              @click="() => selectDimensions(selectableDimensions)">
              {{ capitalizeTranslation('actions.selectAll') }}
            </button>
            <button
              id="remove-all-dimensions"
              class="btn btn-xs btn-danger"
              @click="() => selectDimensions([...dimensionValues], false)">
              {{ capitalizeTranslation('actions.removeAll') }}
            </button>
          </div>
          <div>
            <hr class="mt-0" />
          </div>
          <div class="dimension-container">
            <deal-spend-steering-dimension-card
              v-for="dimension in selectableDimensions"
              :key="dimension"
              :dimension-value="dimension"
              :dimension-type="dealSpendSteeringDimensionType"
              :suppliers="suppliers"
              :deal="deal"
              :active-deal="activeDeal"
              :is-read-only="isReadOnly"
              @select-dimensions="({ values, isAdd }) => selectDimensions(values, isAdd)" />
          </div>
        </div>
      </div>
      <div
        ref="steeredDealsRef"
        class="steered-dimensions"
        :class="{ 'full-span': isReadOnly }">
        <div class="table-heading">
          <span>
            {{ capitalizeTranslation('messages.dimensionState', [i18n.t('messages.steered'), dimensionType]) }}
          </span>
          <div>
            <span>{{ capitalizeTranslation('names.weight') }}: </span>
            <span
              :class="{
                'text-danger':
                  (dealSpendSteering.remainingSteeringPercentage === 0 && selectableDimensionEntities.length > 0) ||
                  dealSpendSteering.totalSteeringPercentage === 0,
              }"
              >{{ dealSpendSteering.totalSteeringPercentageFormatted }}</span
            >
          </div>
        </div>
        <div class="steered-dimensions-list">
          <deal-spend-steering-dimension-card
            v-for="dimension in dealSpendSteeringDimensions.filter((x) => x.dimensionValue !== '*')"
            :key="dimension.dimensionValue"
            :data-dimension-id="dimension.dimensionValue"
            :dimension-value="dimension.dimensionValue"
            :dimension-type="dealSpendSteeringDimensionType"
            :suppliers="suppliers"
            :deal="deal"
            :active-deal="activeDeal"
            :is-read-only="isReadOnly"
            @select-dimensions="({ values, isAdd }) => selectDimensions(values, isAdd)">
            <template #controls>
              <template v-if="isReadOnly">
                <div
                  v-if="dimension.highPriority"
                  class="read-only-high-priority badge badge-success py-2">
                  {{ capitalizeTranslation('messages.highPriority').toUpperCase() }}
                </div>
                <div class="read-only-weight">
                  {{ formatNumber(dimension.steeringPercentage / 100, '0.00%') }}
                </div>
              </template>
              <template v-else>
                <div
                  class="prioritize-dimension-btn flex flex-row align-self-center align-content-center align-self-center">
                  <button
                    :class="dimension.highPriority ? 'btn-success-reverse' : 'btn-info-hollow'"
                    class="btn btn-xs"
                    @click="
                      () => {
                        updateSteeringPriority(dimension.dimensionValue, !dimension.highPriority);
                      }
                    ">
                    {{ capitalizeTranslation(dimension.highPriority ? 'actions.prioritized' : 'actions.prioritize') }}
                  </button>
                </div>
                <formatted-input
                  name="dimensionWeightValue"
                  :value="dimension.steeringPercentage"
                  :is-read-only="isReadOnly"
                  :disabled="isReadOnly"
                  :is-money="false"
                  :is-percentage="true"
                  :input-type="'number'"
                  :validation-errors="isReadOnly ? null : errors"
                  :error-name="isReadOnly ? '' : getErrorName(dimension.dimensionValue)"
                  :max="remainingSteeringPercentage + dimension.steeringPercentage"
                  style="width: 8rem"
                  @value-changed="
                    (val) => {
                      updateSteeringPercentage(dimension, val);
                    }
                  " />
              </template>
            </template>
          </deal-spend-steering-dimension-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, inject, nextTick, Ref, ref, useTemplateRef, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import FormattedInput from '@/common/components/controls/FormattedInput.vue';
  import DealSpendSteeringDimensionCard from '@/common/components/deals/form/spendSteering/DealSpendSteeringDimensionCard.vue';
  import { IconCircleX, IconSearch, IconWarningHexagon } from '@/common/icons/monochrome';
  import { Organization, Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import {
    ISpendSteeringDimension,
    MINIMUM_STEERING_PERCENTAGE,
    SelectableDimensionType,
  } from '@/common/models/package/SpendSteering';
  import { capitalizeTranslation, formatNumber, useInjected } from '@/globals';
  import { useDealComputedProperties } from '@/org-app/services/computedProperties/DealComputedProperties';
  import { mapActions } from '@/utils/StateMapping';
  import MessageCollection from '@/validator/MessageCollection';

  //#region Props

  interface IProps {
    suppliers: MediaOwner[];
    activeDeal?: Package;
    isReadOnly?: boolean;
  }

  const { suppliers, activeDeal = null, isReadOnly = false } = defineProps<IProps>();

  const deal: Ref<Package> = useInjected<Package>('deal');
  const errors: Ref<MessageCollection> = inject('errors', null);

  //#endregion

  //#region State

  const { updatePackageSpendSteering } = mapActions();

  const i18n = useI18n();

  const emits = defineEmits(['selectedDimensionsChanged']);

  const steeredDealsRef = useTemplateRef('steeredDealsRef');
  const adaptedCardSizeStyle = ref({ maxHeight: '360px' });
  const searchInput = ref('');

  const spendSteeringErrorNames = [
    'dealSpendSteeringAtLeastOneDimensionMustBeSelected',
    'spendSteeringAllSteeringPercentagesMustBeGreaterThanZero',
    'dealSpendSteeringTotalPercentage',
    'dealSpendSteeringActiveWildCardTotalPercentage',
  ];

  //#endregion

  //#region Computed

  const { dealSpendSteeringDimensionType, dealSpendSteering } = useDealComputedProperties(deal);

  const selectedDeals = computed(() => {
    return deal.value.packages ?? [];
  });

  const dimensionValues = computed(() => {
    return new Set(
      dealSpendSteeringDimensions.value.filter((d) => d.dimensionValue !== '*').map((d) => d.dimensionValue),
    );
  });

  const selectableDeals = computed(() => {
    return selectedDeals.value?.filter((p) => {
      return !dimensionValues.value.has(p.dealID);
    });
  });

  const selectablePublishers = computed(() => {
    const publisherIDs = new Set(
      deal.value.packages.map((x) => String(x.organizationID)).filter((x) => !dimensionValues.value.has(String(x))),
    );
    return suppliers.filter((mo) => publisherIDs.has(String(mo.id)));
  });

  const selectableDimensionEntities = computed(() => {
    return dealSpendSteeringDimensionType.value === SelectableDimensionType.DEAL
      ? selectableDeals.value
      : selectablePublishers.value;
  });

  const selectableDimensions = computed(() => {
    const getMappingProperty = (entity: Package | Organization) => {
      return dealSpendSteeringDimensionType.value === SelectableDimensionType.DEAL
        ? (entity as Package).dealID
        : String((entity as Organization).id);
    };
    if (!searchInput.value.trim()) {
      return selectableDimensionEntities.value.map(getMappingProperty);
    }
    const searchQuery = searchInput.value.toLowerCase();
    return selectableDimensionEntities.value
      .filter(
        (entity) => entity.name.toLowerCase().includes(searchQuery) || getMappingProperty(entity).includes(searchQuery),
      )
      .map(getMappingProperty);
  });

  const dealSpendSteeringDimensions = computed({
    get() {
      return dealSpendSteering.value?.spendSteeringDimensions || [];
    },
    set(value: ISpendSteeringDimension[]) {
      // To maintain reactivity, we need to clone the object and assign it back to the deal.
      const updatedDealSpendSteering = dealSpendSteering.value.clone();
      updatedDealSpendSteering.spendSteeringDimensions = value;
      updatePackageSpendSteering({
        package: deal.value,
        value: updatedDealSpendSteering,
      });
    },
  });

  // Used to calculate the remaining steering percentage to prevent overallocation
  const remainingSteeringPercentage = computed(() => {
    return dealSpendSteering.value.availableSteeringPercentage;
  });

  const warningMessages = computed(() => {
    const activeSteeringDimensions = dealSpendSteeringDimensions.value.filter((x) => x.dimensionValue !== '*');
    const messages = [];
    if (dealSpendSteeringDimensions.value.length === 0) {
      messages.push(capitalizeTranslation('messages.dealSpendSteeringAtLeastOneDimensionMustBeSelected'));
    }
    if (selectableDimensions.value.length === 0) {
      messages.push(capitalizeTranslation('messages.dealSpendSteeringTotalPercentage'));
    }
    if (selectableDimensions.value.length > 0 && activeSteeringDimensions.length > 0) {
      messages.push(capitalizeTranslation('messages.dealSpendSteeringActiveWildCardTotalPercentage'));
      messages.push(capitalizeTranslation('messages.dealSpendSteeringActiveWildCard'));
    }
    if (activeSteeringDimensions.some((dimension) => dimension.steeringPercentage < MINIMUM_STEERING_PERCENTAGE)) {
      messages.push(capitalizeTranslation('messages.spendSteeringAllSteeringPercentagesMustBeGreaterThanZero'));
    }

    return messages;
  });

  const dimensionType = computed(() => {
    return i18n.t(
      `names.${dealSpendSteeringDimensionType.value === SelectableDimensionType.DEAL ? 'deal' : 'mediaOwner'}`,
      2,
    );
  });

  const searchForEntity = computed(() => {
    return capitalize(i18n.t('actions.searchForEntity', [dimensionType.value]));
  });

  const errorKeys = computed(() => {
    return Object.keys(errors.value?.messages || {});
  });

  const hasSteeringErrors = computed(() => {
    if (!errorKeys.value.length) return [];
    return errorKeys.value.filter((errorKey) => spendSteeringErrorNames.includes(errorKey));
  });

  //#endregion

  //#region Methods

  // Updates the steering percentage of a specific dimension
  function updateSteeringPercentage(dimension: ISpendSteeringDimension, value: number) {
    let updatedPercentage = parseFloat(value.toFixed(2));
    let hasChange = false;
    const remainingPercentage = remainingSteeringPercentage.value + dimension.steeringPercentage;
    if (value < 0 || remainingPercentage <= 0) return;
    if (updatedPercentage > remainingPercentage) {
      updatedPercentage = remainingPercentage;
    }
    // Iterate through the dimensions and update the matching one
    const updatedSteeringDimensions = dealSpendSteeringDimensions.value.map((d) => {
      if (d.dimensionValue === dimension.dimensionValue && d.steeringPercentage !== updatedPercentage) {
        hasChange = true;
        return { ...d, steeringPercentage: updatedPercentage };
      }
      return d;
    });
    if (hasChange) {
      dealSpendSteeringDimensions.value = updatedSteeringDimensions;
    }
  }

  // Updates the steering priority of a specific dimension
  function updateSteeringPriority(dimensionValue: string, value: boolean) {
    let hasChange = false;
    const updatedSteeringDimensions = dealSpendSteeringDimensions.value.map((dimension) => {
      if (dimension.dimensionValue === dimensionValue && dimension.highPriority !== value) {
        hasChange = true;
        return { ...dimension, highPriority: value };
      }
      return dimension;
    });
    if (hasChange) {
      dealSpendSteeringDimensions.value = updatedSteeringDimensions;
    }
  }

  function getErrorName(dimensionValue: string) {
    if (isReadOnly || !hasSteeringErrors.value.length) return '';
    if (errorKeys.value.includes('spendSteeringAllSteeringPercentagesMustBeGreaterThanZero')) {
      const dimension = dealSpendSteeringDimensions.value.find((d) => d.dimensionValue === dimensionValue);
      return dimension?.steeringPercentage < MINIMUM_STEERING_PERCENTAGE
        ? 'spendSteeringAllSteeringPercentagesMustBeGreaterThanZero'
        : '';
    }
    return errorKeys.value.some((errorKey) => spendSteeringErrorNames.includes(errorKey)) ? errorKeys.value[0] : '';
  }

  function selectDimensions(values: string[], isAdd = true) {
    const existingDimensions = dealSpendSteeringDimensions.value.map((x) => x.dimensionValue);
    const updatedDimensions = isAdd
      ? [...existingDimensions, ...values]
      : existingDimensions.filter((x) => !values.includes(x));
    emits('selectedDimensionsChanged', updatedDimensions);
  }

  //#endregion

  //#region Watchers

  watch(
    () => dealSpendSteeringDimensions.value.length,
    async (newLength) => {
      // Need to use next tick since the height doesn't always update right away.
      await nextTick();
      if (!steeredDealsRef.value) return;
      if (steeredDealsRef.value && newLength >= 4) {
        adaptedCardSizeStyle.value = {
          maxHeight: `${steeredDealsRef.value.clientHeight}px`,
        };
      } else {
        adaptedCardSizeStyle.value = { maxHeight: '365px' };
      }
    },
    { deep: true, immediate: true },
  );

  //#endregion
</script>

<style scoped lang="scss" rel="stylesheet/scss">
  @use '@/styles/variables' as *;

  .spend-steering-tables {
    display: grid;
    grid-template-columns: 1fr 3fr;
    grid-template-rows: 1fr;
    grid-column-gap: 2rem;

    @media (max-width: 1750px) {
      grid-template-columns: 2fr 3.75fr;
    }

    @media (max-width: 1375px) {
      grid-template-columns: 1fr;
      grid-template-rows: 1fr 1fr;
      grid-row-gap: 2rem;
    }
  }

  .full-span {
    grid-column: span 2;
    grid-row: span 2;
  }

  .table-heading {
    font-size: 1.4rem;
    padding: 1rem;
    background-color: rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-content: center;

    p {
      font-family: $font-family;
      margin: 0;
    }
  }

  .available-dimensions {
    display: flex;
    min-height: 365px;
    flex-direction: column;

    .available-dimension-list {
      display: flex;
      overflow: hidden;
      flex-direction: column;
      max-height: 100%;

      .dimension-container {
        flex-grow: 1;
        max-height: 100%;
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        padding-bottom: 1rem;
      }
    }
  }

  .steered-dimensions-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 1rem 0;
  }

  .available-dimensions,
  .steered-dimensions {
    background-color: $almost-white;
    border: 1px solid lightgrey;
  }

  .input-container {
    position: relative;

    .table-text-filter {
      border-radius: 0;
    }
  }

  .delete {
    display: flex;
    align-items: center;
    position: absolute;
    top: 0;
    right: 0.5rem;
    bottom: 0;
    justify-content: center;
    margin-right: 0.6rem;

    div {
      display: flex;
      cursor: pointer;
    }

    svg {
      fill: #000;
      stroke: #000;
      opacity: 0.2;
      width: 1.6rem !important;
      height: 1.6rem !important;
    }
  }
</style>
